import { Sparkles } from 'lucide-react';

/**
 * Static end-card page for the 45-second AIgnyx demo video.
 * Renders the same design at any viewport — screenshot at
 * 1920×1080 for landscape and 1080×1920 for vertical exports.
 *
 * Access:
 *   /end-card         → responsive (fits current viewport)
 *   /end-card?v=9x16  → forces 9:16 vertical layout (larger type)
 */
export default function EndCardPage() {
  const isVertical =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('v') === '9x16';

  return (
    <div
      className="relative w-screen h-screen overflow-hidden flex items-center justify-center"
      style={{ background: '#0A0A14' }}
      data-testid="end-card"
    >
      {/* Radial purple glow — center-bottom */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 90% 60% at 50% 90%, rgba(90,80,229,0.55) 0%, rgba(90,80,229,0.15) 40%, transparent 70%)',
        }}
      />

      {/* Subtle secondary glow — top corner */}
      <div
        className="absolute inset-0 opacity-40"
        style={{
          background:
            'radial-gradient(circle at 20% 15%, rgba(120,110,255,0.35) 0%, transparent 45%)',
        }}
      />

      {/* Noise overlay */}
      <div
        className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center px-6 text-center">
        {/* Logo lockup */}
        <div className="flex items-center gap-3 mb-10 sm:mb-14 opacity-90">
          <div
            className="rounded-2xl flex items-center justify-center shadow-lg"
            style={{
              width: isVertical ? '68px' : '52px',
              height: isVertical ? '68px' : '52px',
              background: 'linear-gradient(135deg, #5A50E5 0%, #7B6FFF 100%)',
              boxShadow: '0 8px 32px rgba(90,80,229,0.5)',
            }}
          >
            <Sparkles
              className="text-white"
              style={{ width: isVertical ? '32px' : '24px', height: isVertical ? '32px' : '24px' }}
            />
          </div>
          <span
            className="font-heading font-bold text-white tracking-tight"
            style={{ fontSize: isVertical ? '38px' : '28px' }}
          >
            AIgnyx
          </span>
        </div>

        {/* URL */}
        <h1
          className="font-heading font-bold text-white tracking-tight leading-none mb-6 sm:mb-8"
          style={{ fontSize: isVertical ? '104px' : '128px' }}
        >
          aignyx<span style={{ color: '#7B6FFF' }}>.</span>com
        </h1>

        {/* Tagline */}
        <p
          className="text-white/60 font-light tracking-wide max-w-2xl leading-snug"
          style={{ fontSize: isVertical ? '30px' : '26px' }}
        >
          AI ad content that converts.
          <br />
          No agency required.
        </p>

        {/* Tiny brand line — legal-safe */}
        <p
          className="text-white/25 mt-16 sm:mt-24 tracking-widest uppercase"
          style={{
            fontSize: isVertical ? '14px' : '11px',
            letterSpacing: isVertical ? '0.25em' : '0.2em',
          }}
        >
          Beta · Free during launch
        </p>
      </div>

      {/* Corner accents (fine detail) */}
      <div
        className="absolute top-8 left-8 opacity-30"
        style={{
          width: '48px',
          height: '48px',
          borderTop: '1px solid rgba(255,255,255,0.4)',
          borderLeft: '1px solid rgba(255,255,255,0.4)',
        }}
      />
      <div
        className="absolute bottom-8 right-8 opacity-30"
        style={{
          width: '48px',
          height: '48px',
          borderBottom: '1px solid rgba(255,255,255,0.4)',
          borderRight: '1px solid rgba(255,255,255,0.4)',
        }}
      />
    </div>
  );
}
